import { withProperty } from '../PropertyProvider';
import { withTheme } from '../../../shared/ThemeProvider';
import Loading from '../../../shared/Loading';
import React from 'react';
import PropertySubmenu from './PropertySubmenu';
import PropertyDropdownSelector from '../../shared/PropertyDropdownSelector';
import formatAddressToString from '../../../utils/formatAddressToString';
import {
	getPropertyScopedNavItems,
	resolvePropertyPath,
} from './propertyScopedNavigation';

/**
 * @typedef {{
 *  key: string,
 *  label: string,
 *  path: string
 * }} ScopedNavItem
 */

/**
 * @typedef {{
 *  location: { pathname: string, search?: string, hash?: string },
 *  history: { push: (location: { pathname: string, search?: string, hash?: string }) => void },
 *  match: { params: { propertyId?: string } },
 *  children?: React.ReactNode
 * } & import('../PropertyProvider/PropertyContext').PropType
 *   & import('../../../shared/ThemeProvider/Context').PropType} PropertyScopedLayoutProps
 */

/** @extends {React.Component<PropertyScopedLayoutProps, {isSyncingProperty: boolean}>} */
class PropertyScopedLayout extends React.Component {
	/** @param {PropertyScopedLayoutProps} props */
	constructor(props) {
		super(props);

		this.state = {
			isSyncingProperty: false,
		};
		this.handlePropertyChange = this.handlePropertyChange.bind(this);
	}

	async componentDidMount() {
		await this.syncSelectedProperty();
	}

	/**
	 * @param {PropertyScopedLayoutProps} prevProps
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps) {
		if (
			prevProps.match.params.propertyId !==
			this.props.match.params.propertyId
		) {
			await this.syncSelectedProperty();
		}
	}

	async syncSelectedProperty() {
		const propertyId = this.props.match.params.propertyId;

		if (!propertyId) {
			return;
		}

		if (
			this.props.property.propertyList?.length &&
			!this.props.property.propertyList.some(
				(property) => property.id === propertyId
			)
		) {
			return;
		}

		if (this.props.property.selectedProperty?.id === propertyId) {
			return;
		}

		this.setState({ isSyncingProperty: true });

		try {
			await this.props.property.setSelectedProperty(propertyId);
		} finally {
			this.setState({ isSyncingProperty: false });
		}
	}

	/**
	 * @param {string} propertyId
	 * @returns {void}
	 */
	handlePropertyChange(propertyId) {
		const currentPropertyId = this.props.match.params.propertyId;
		this.props.history.push({
			hash: this.props.location.hash,
			pathname: this.props.location.pathname.replace(
				`/${currentPropertyId}/`,
				`/${propertyId}/`
			),
			search: this.props.location.search,
		});
	}

	// eslint-disable-next-line complexity -- coordinates route, property, and team readiness
	render() {
		const propertyId = this.props.match.params.propertyId;
		const hasTeam = Boolean(
			this.props.theme?.agentId && this.props.theme?.teamList?.length
		);
		const selectedProperty = this.props.property.propertyList?.find(
			(property) => property.id === propertyId
		);
		const isKnownRouteProperty =
			!this.props.property.propertyList?.length ||
			this.props.property.propertyList.some(
				(property) => property.id === propertyId
			);
		const isPropertyReady =
			!isKnownRouteProperty ||
			(Boolean(propertyId) &&
				this.props.property.selectedProperty?.id === propertyId);
		const activeNavItem = getPropertyScopedNavItems(hasTeam).find(
			(item) =>
				Boolean(propertyId) &&
				this.props.location.pathname ===
					resolvePropertyPath(item.path, propertyId)
		);

		return (
			<section className="mt-4">
				{activeNavItem ? (
					<h1 className="sr-only">{activeNavItem.label}</h1>
				) : null}
				<section className="border-y border-grey-300 bg-white py-2 mb-6">
					<section className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between">
						<PropertySubmenu
							activePath={this.props.location.pathname}
							className="min-w-0 flex-1"
							hasTeam={hasTeam}
							propertyId={propertyId}
						/>
						<PropertyDropdownSelector
							className="fancy-dropdown w-full lg:w-80 lg:flex-none xl:w-96"
							formatPropertyLabel={(
								/** @type {import('../PropertyProvider/State').Property} */ property
							) => formatAddressToString(property.address)}
							idPrefix="homeowner-property-selector"
							onSelect={this.handlePropertyChange}
							propertyList={
								this.props.property.propertyList ?? []
							}
							selectedProperty={selectedProperty}
						/>
					</section>
				</section>

				{this.state.isSyncingProperty || !isPropertyReady ? (
					<Loading />
				) : (
					this.props.children
				)}
			</section>
		);
	}
}

export default withTheme(withProperty(PropertyScopedLayout));
